import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowPathIcon, PlayIcon, VideoCameraIcon, PhotoIcon, SparklesIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';
import { userAPI, crmAPI, API_BASE_URL } from '../../services/api';
import toast from 'react-hot-toast';
import { CustomRotatedVideoPlayer } from '../common/CustomRotatedVideoPlayer';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { updateUser } from '../../store/slices/authSlice';
import { IS_INSTAGRAM_SYNC_ENABLED } from '../../utils/featureFlags';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

interface InstagramImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccessPhotos?: (imported: number, skipped: number) => void;
  onSuccessProject?: (preFillData: any) => void;
  initialMode?: 'photos' | 'project';
}

export const InstagramImportModal: React.FC<InstagramImportModalProps> = ({
  isOpen,
  onClose,
  onSuccessPhotos,
  onSuccessProject,
  initialMode,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [instagramFeed, setInstagramFeed] = useState<any[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextAfterCursor, setNextAfterCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedFeedItemIds, setSelectedFeedItemIds] = useState<Set<string>>(new Set());
  const [syncType, setSyncType] = useState<'photos' | 'project'>(() => {
    if (initialMode) return initialMode;
    if (onSuccessProject && !onSuccessPhotos) return 'project';
    return 'photos';
  });
  const [syncing, setSyncing] = useState(false);

  // Reel & Video Rotation state (mediaId -> rotation degree 0, 90, 180, 270)
  const [filterTab, setFilterTab] = useState<'all' | 'photos' | 'reels'>('all');
  const [mediaRotations, setMediaRotations] = useState<Record<string, number>>({});
  const [previewItem, setPreviewItem] = useState<any | null>(null);

  const [driveConnected, setDriveConnected] = useState(false);
  const [driveAuthUrl, setDriveAuthUrl] = useState('');
  const [accessDeniedByBackend, setAccessDeniedByBackend] = useState(false);
  const [showDrivePromptModal, setShowDrivePromptModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialMode) {
        setSyncType(initialMode);
      } else if (onSuccessProject && !onSuccessPhotos) {
        setSyncType('project');
      } else {
        setSyncType('photos');
      }
      loadInitialFeed();
      checkDriveStatus();
    } else {
      // Reset states on close
      setInstagramFeed([]);
      setSelectedFeedItemIds(new Set());
      setNextAfterCursor(null);
      setHasMore(true);
      setFilterTab('all');
      setMediaRotations({});
      setPreviewItem(null);
    }
  }, [isOpen]);

  const checkDriveStatus = async () => {
    try {
      const res = await crmAPI.getGoogleDriveStatus();
      setDriveConnected(res.data?.connected || false);
      if (!res.data?.connected) {
        const authRes = await crmAPI.getGoogleDriveAuthURL();
        setDriveAuthUrl(authRes.data?.url || '');
      }
    } catch (e) {
      console.error('Failed to check Google Drive status', e);
    }
  };

  const loadInitialFeed = async (forceFetch = false) => {
    if (!user?.instagram_access_token) return;
    if (!forceFetch && user?.instagram_media_access_enabled !== true) return;
    setLoadingFeed(true);
    setAccessDeniedByBackend(false);
    setNextAfterCursor(null);
    setHasMore(true);
    setSelectedFeedItemIds(new Set());
    try {
      const response = await userAPI.getInstagramFeed();
      const data = response.data.data || response.data || [];
      const paging = response.data.paging;
      setInstagramFeed(data);
      if (paging?.cursors?.after) {
        setNextAfterCursor(paging.cursors.after);
        setHasMore(true);
      } else {
        setNextAfterCursor(null);
        setHasMore(false);
      }
    } catch (error: any) {
      if (error.response?.data?.error === 'instagram_not_connected') {
        return;
      }
      const errCode = error.response?.data?.code || error.response?.data?.error;
      const errStr = String(error.response?.data?.message || errCode || '');
      if (
        error.response?.status === 403 ||
        errCode === 'instagram_media_access_disabled' ||
        errCode === 'INSTAGRAM_MEDIA_ACCESS_DISABLED' ||
        errStr.toLowerCase().includes('disabled')
      ) {
        setAccessDeniedByBackend(true);
        dispatch(updateUser({ instagram_media_access_enabled: false }));
        return;
      }
      toast.error(errStr || 'Failed to retrieve Instagram feed');
      onClose();
    } finally {
      setLoadingFeed(false);
    }
  };

  const loadMoreFeed = async () => {
    if (!nextAfterCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await userAPI.getInstagramFeed(nextAfterCursor);
      const data = response.data.data || response.data || [];
      const paging = response.data.paging;

      setInstagramFeed((prev) => [...prev, ...data]);
      if (paging?.cursors?.after) {
        setNextAfterCursor(paging.cursors.after);
        setHasMore(true);
      } else {
        setNextAfterCursor(null);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more feed items:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRotateItem = (itemId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setMediaRotations((prev) => {
      const current = prev[itemId] || 0;
      const next = (current + 90) % 360;
      return { ...prev, [itemId]: next };
    });
  };

  const handleSyncSubmit = async () => {
    if (selectedFeedItemIds.size === 0) return;

    const selectedIds = Array.from(selectedFeedItemIds);
    const selectedItems = instagramFeed.filter((x) => selectedIds.includes(x.id));
    const hasVideos = selectedItems.some(
      (x) => x.media_type === 'VIDEO' || (x.media_type === 'CAROUSEL_ALBUM' && x.children?.data?.some((c: any) => c.media_type === 'VIDEO'))
    );

    if (hasVideos && !driveConnected) {
      if (!driveAuthUrl) {
        try {
          const authRes = await crmAPI.getGoogleDriveAuthURL();
          setDriveAuthUrl(authRes.data?.url || '');
        } catch (e) {}
      }
      setShowDrivePromptModal(true);
      return;
    }

    if (syncType === 'photos') {
      setSyncing(true);
      try {
        const response = await userAPI.syncInstagram(selectedIds, mediaRotations);
        const { imported, skipped } = response.data;
        if (onSuccessPhotos) {
          onSuccessPhotos(imported, skipped);
        } else {
          toast.success(`Instagram sync completed! Imported ${imported} item(s), skipped ${skipped} duplicate(s).`);
        }
        onClose();
      } catch (error: any) {
        if (
          error.response?.data?.code === 'CLOUDRIVE_NOT_CONNECTED' ||
          error.response?.data?.error?.includes('Google Drive')
        ) {
          setDriveConnected(false);
          if (!driveAuthUrl) {
            try {
              const authRes = await crmAPI.getGoogleDriveAuthURL();
              setDriveAuthUrl(authRes.data?.url || '');
            } catch (e) {}
          }
          setShowDrivePromptModal(true);
          return;
        }
        toast.error(error.response?.data?.error || 'Failed to sync Instagram items');
      } finally {
        setSyncing(false);
      }
    } else {
      // Import as project (aggregate media & video URLs from all selected items)
      setSyncing(true);
      (async () => {
        try {
          const selectedIds = Array.from(selectedFeedItemIds);
          const selectedItems = instagramFeed.filter((x) => selectedIds.includes(x.id));
          if (selectedItems.length > 0) {
            const mediaItem = selectedItems[0]; // Primary item for metadata

            // Check if any items are videos that need to be uploaded to Google Drive
            const hasVideos = selectedItems.some(
              (item) =>
                item.media_type === 'VIDEO' ||
                (item.media_type === 'CAROUSEL_ALBUM' &&
                  item.children?.data?.some((c: any) => c.media_type === 'VIDEO'))
            );

            let syncedItemsMap: Record<string, any> = {};
            if (hasVideos) {
              try {
                const syncRes = await userAPI.syncInstagram(selectedIds, mediaRotations, syncType);
                if (syncRes.data?.items) {
                  syncRes.data.items.forEach((si: any) => {
                    if (si.video_id) syncedItemsMap[si.video_id] = si;
                    if (si.id) syncedItemsMap[si.id] = si;
                    if (si.instagram_id) syncedItemsMap[si.instagram_id] = si;
                  });
                }
              } catch (syncErr: any) {
                console.error('Failed to sync Reel to Drive for project', syncErr);
                if (
                  syncErr.response?.data?.code === 'CLOUDRIVE_NOT_CONNECTED' ||
                  syncErr.response?.data?.error?.includes('Google Drive')
                ) {
                  setDriveConnected(false);
                  if (!driveAuthUrl) {
                    try {
                      const authRes = await crmAPI.getGoogleDriveAuthURL();
                      setDriveAuthUrl(authRes.data?.url || '');
                    } catch (e) {}
                  }
                  setShowDrivePromptModal(true);
                  setSyncing(false);
                  return;
                }
              }
            }

            // Clean title
            const cleanTitle = (caption: string) => {
              if (!caption) return 'Instagram Project';
              const words = caption.split(/\s+/);
              const titleWords: string[] = [];
              for (const w of words) {
                if (!w.startsWith('#')) titleWords.push(w);
              }
              if (titleWords.length === 0) return 'Instagram Project';
              const title = titleWords.join(' ');
              return title.length > 40 ? title.substring(0, 37) + '...' : title;
            };

            const extractHashtags = (caption: string) => {
              if (!caption) return [];
              const matches = caption.match(/#\w+/g);
              return matches ? matches.map((m) => m.substring(1).toLowerCase()) : [];
            };

            const images: string[] = [];
            const videos: { url: string; thumbnail?: string; rotation?: number }[] = [];

            selectedItems.forEach((item) => {
              const itemRotation = mediaRotations[item.id] || 0;
              const synced = syncedItemsMap[item.id];
              if (item.media_type === 'VIDEO') {
                const videoUrl = synced?.youtube_url || item.media_url;
                videos.push({
                  url: videoUrl,
                  thumbnail: synced?.thumbnail_url || item.thumbnail_url || item.media_url,
                  rotation: itemRotation,
                });
              } else if (item.media_type === 'CAROUSEL_ALBUM' && item.children?.data) {
                item.children.data.forEach((child: any) => {
                  if (child.media_type === 'VIDEO') {
                    const childSynced = syncedItemsMap[child.id] || synced;
                    videos.push({
                      url: childSynced?.youtube_url || child.media_url,
                      thumbnail: childSynced?.thumbnail_url || child.thumbnail_url || child.media_url,
                      rotation: itemRotation,
                    });
                  } else {
                    images.push(child.media_url);
                  }
                });
              } else {
                images.push(item.media_url);
              }
            });

            const preFill = {
              title: cleanTitle(mediaItem.caption),
              project_description: mediaItem.caption || '',
              tags: extractHashtags(mediaItem.caption || ''),
              images: images,
              videos: videos,
              rotations: mediaRotations,
            };

            if (onSuccessProject) {
              onSuccessProject(preFill);
            }
            onClose();
          }
        } finally {
          setSyncing(false);
        }
      })();
    }
  };

  if (!isOpen || !IS_INSTAGRAM_SYNC_ENABLED) return null;

  if (!user?.instagram_access_token) {
    return ReactDOM.createPortal(
      <AnimatePresence>
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-md p-6 shadow-2xl border border-gray-100 dark:border-gray-800 text-center relative overflow-hidden"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400 hover:text-gray-600 transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-yellow-500 via-red-500 to-purple-600 flex items-center justify-center text-white mx-auto mb-4 shadow-lg shadow-purple-500/20">
              <svg className="w-9 h-9" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Connect Instagram Account
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
              Connect your Instagram Creator or Business account to import your photos &amp; reels into ClickCollab.
            </p>

            <div className="p-3.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900 text-xs text-indigo-900 dark:text-indigo-200 mb-6 text-left leading-normal">
              💡 <strong>Note:</strong> Once authorized, you will be taken to <strong>Settings &gt; Connections</strong> where you can turn ON <em>Allow media access</em> to start syncing.
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="w-1/2">
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const token = localStorage.getItem('token');
                  if (!token) {
                    toast.error('Session expired. Please log in again.');
                    return;
                  }
                  const url = `${API_BASE_URL}/instagram/connect?token=${token}`;
                  if (Capacitor.isNativePlatform()) {
                    await Browser.open({ url, windowName: '_self' });
                  } else {
                    window.location.href = url;
                  }
                }}
                className="w-1/2 bg-gradient-to-r from-yellow-500 via-red-500 to-purple-600 hover:opacity-90 border-none text-white font-medium"
              >
                Connect Now
              </Button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>,
      document.body
    );
  }

  if (user?.instagram_media_access_enabled !== true || accessDeniedByBackend) {
    return ReactDOM.createPortal(
      <AnimatePresence>
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-md p-6 shadow-2xl border border-gray-100 dark:border-gray-800 text-center relative overflow-hidden"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400 hover:text-gray-600 transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mx-auto mb-4 border border-indigo-100 dark:border-indigo-900 shadow-sm">
              <PhotoIcon className="w-8 h-8" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Allow Media Access
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              ClickCollab needs permission to access your Instagram photos and reels to sync them into your portfolio.
            </p>

            <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 mb-6 text-center">
              🔒 You can disable media access anytime in your <strong>Account Settings &gt; Connections</strong>.
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="w-1/2">
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await userAPI.updateProfile({ instagram_media_access_enabled: true });
                    dispatch(updateUser({ instagram_media_access_enabled: true }));
                    setAccessDeniedByBackend(false);
                    toast.success('Media access allowed!');
                    loadInitialFeed(true);
                  } catch (e: any) {
                    toast.error('Failed to enable media access');
                  }
                }}
                className="w-1/2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
              >
                Allow Access
              </Button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>,
      document.body
    );
  }

  const filteredFeed = instagramFeed.filter((item) => {
    if (filterTab === 'photos') {
      return item.media_type === 'IMAGE' || item.media_type === 'CAROUSEL_ALBUM';
    }
    if (filterTab === 'reels') {
      return item.media_type === 'VIDEO';
    }
    return true;
  });

  const photosCount = instagramFeed.filter((x) => x.media_type === 'IMAGE' || x.media_type === 'CAROUSEL_ALBUM').length;
  const reelsCount = instagramFeed.filter((x) => x.media_type === 'VIDEO').length;

  const hasSelectedCarousel = instagramFeed.some((item) => selectedFeedItemIds.has(item.id) && item.media_type === 'CAROUSEL_ALBUM');

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-4xl max-h-[88vh] flex flex-col shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              Import from Instagram
              <SparklesIcon className="w-5 h-5 text-indigo-500" />
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Select Photos and Reels to sync into your portfolio or profile project
            </p>
            <div className="mt-2 flex items-center space-x-2 text-xs">
              {driveConnected ? (
                <span className="inline-flex items-center text-emerald-600 dark:text-emerald-400 font-medium bg-emerald-50 dark:bg-emerald-900/30 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-800">
                  Google Drive Connected ✓ (Reels upload directly to your Drive)
                </span>
              ) : (
                <a
                  href={driveAuthUrl || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-amber-600 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-900/30 px-2.5 py-1 rounded-full border border-amber-200 dark:border-amber-800 hover:underline"
                >
                  Connect Google Drive (to upload Reels directly to your Drive) →
                </a>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400 hover:text-gray-600 transition"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Filter Navigation Tabs */}
        <div className="flex items-center px-6 pt-4 space-x-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
          <button
            onClick={() => setFilterTab('all')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-xl transition-all border-b-2 ${
              filterTab === 'all'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-900 shadow-xs'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            All Media ({instagramFeed.length})
          </button>
          <button
            onClick={() => setFilterTab('photos')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-xl transition-all border-b-2 flex items-center space-x-1.5 ${
              filterTab === 'photos'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-900 shadow-xs'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <PhotoIcon className="w-4 h-4" />
            <span>Photos ({photosCount})</span>
          </button>
          <button
            onClick={() => setFilterTab('reels')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-xl transition-all border-b-2 flex items-center space-x-1.5 ${
              filterTab === 'reels'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-900 shadow-xs'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <VideoCameraIcon className="w-4 h-4 text-purple-500" />
            <span>Reels & Videos ({reelsCount})</span>
          </button>
        </div>

        {/* Modal Content / Media Grid */}
        <div
          className="flex-1 overflow-y-auto p-6 min-h-[300px]"
          onScroll={(e) => {
            const target = e.currentTarget;
            const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
            if (isNearBottom && hasMore && !loadingMore && !loadingFeed) {
              loadMoreFeed();
            }
          }}
        >
          {loadingFeed ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
              <span className="text-gray-500">Retrieving your Instagram feed...</span>
            </div>
          ) : filteredFeed.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 space-y-2">
              <VideoCameraIcon className="w-12 h-12 text-gray-400" />
              <span>No {filterTab === 'reels' ? 'Reels' : filterTab === 'photos' ? 'Photos' : 'Instagram posts'} found.</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {filteredFeed.map((item) => {
                const isSelected = selectedFeedItemIds.has(item.id);
                const isCarousel = item.media_type === 'CAROUSEL_ALBUM';
                const isVideo = item.media_type === 'VIDEO';
                const rotation = mediaRotations[item.id] || 0;

                return (
                  <div
                    key={item.id}
                    onClick={() => {
                      const newSelection = new Set(selectedFeedItemIds);
                      if (newSelection.has(item.id)) {
                        newSelection.delete(item.id);
                      } else {
                        newSelection.add(item.id);
                      }
                      setSelectedFeedItemIds(newSelection);
                    }}
                    className={`relative aspect-square rounded-2xl overflow-hidden cursor-pointer border-3 transition group bg-gray-900 ${
                      isSelected
                        ? 'border-indigo-600 ring-4 ring-indigo-500/20'
                        : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'
                    }`}
                  >
                    <img
                      src={isVideo && item.thumbnail_url ? item.thumbnail_url : item.media_url}
                      alt={item.caption || 'Instagram Post'}
                      className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                      style={{
                        transform: rotation ? `rotate(${rotation}deg)` : undefined,
                      }}
                    />

                    {/* Play icon overlay for Reel videos */}
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/40 transition">
                        <div className="w-10 h-10 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center shadow-lg group-hover:scale-110 transition">
                          <PlayIcon className="w-6 h-6 text-white ml-0.5" />
                        </div>
                      </div>
                    )}

                    {/* Image/Video Hover Overlay with Actions */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition p-3 flex flex-col justify-between">
                      <div className="flex justify-between items-start">
                        {/* Rotate Action Button (Only for Videos) */}
                        {isVideo ? (
                          <button
                            type="button"
                            onClick={(e) => handleRotateItem(item.id, e)}
                            title="Rotate 90° Clockwise"
                            className="p-1.5 bg-black/60 hover:bg-indigo-600 text-white rounded-lg backdrop-blur-md transition flex items-center gap-1 text-xs font-semibold"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                            <span>{rotation ? `${rotation}°` : 'Rotate'}</span>
                          </button>
                        ) : <div />}

                        {/* Preview Action Button for Videos */}
                        {isVideo && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewItem(item);
                            }}
                            className="px-2 py-1 bg-white/20 hover:bg-white/40 text-white rounded-lg text-xs font-medium backdrop-blur-md"
                          >
                            Preview
                          </button>
                        )}
                      </div>

                      <p className="text-xs text-white line-clamp-2">{item.caption || 'No caption'}</p>
                    </div>

                    {/* Selected Checkmark overlay */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full p-1 shadow z-10">
                        <svg className="w-5 h-5 fill-white text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l3-3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}

                    {/* Reel / Carousel Badges (Both positioned at bottom-left) */}
                    {isVideo && (
                      <div className="absolute bottom-2 left-2 bg-purple-600/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center space-x-1 backdrop-blur-md shadow">
                        <VideoCameraIcon className="w-3 h-3" />
                        <span>Reel</span>
                      </div>
                    )}
                    {isCarousel && (
                      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center space-x-1 backdrop-blur-sm">
                        <span>Carousel</span>
                      </div>
                    )}
                    {rotation > 0 && !isVideo && (
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded backdrop-blur-sm">
                        {rotation}°
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {loadingMore && (
            <div className="flex justify-center items-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
            </div>
          )}
        </div>

        {/* Carousel Photo Sync Warning Notice */}
        {syncType === 'photos' && hasSelectedCarousel && (
          <div className="px-6 py-3 bg-amber-50 dark:bg-amber-950/20 border-t border-amber-100/50 dark:border-amber-900/20 text-xs text-amber-700 dark:text-amber-300 flex items-center space-x-2">
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>Selected carousel(s) will be imported as separate standalone photos.</span>
          </div>
        )}

        {/* Modal Footer */}
        <div className="p-3 sm:p-5 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0 sticky bottom-0 z-20 shadow-lg">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-6">
            <label className="flex items-center space-x-2 text-xs md:text-sm cursor-pointer bg-gray-50 dark:bg-gray-800/60 p-2 sm:p-0 rounded-xl border border-gray-100 dark:border-gray-700 sm:border-0">
              <input
                type="radio"
                name="syncType"
                checked={syncType === 'photos'}
                onChange={() => setSyncType('photos')}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-gray-700 dark:text-gray-300 font-medium truncate">Import Photos & Reels</span>
            </label>
            <label className="flex items-center space-x-2 text-xs md:text-sm cursor-pointer bg-gray-50 dark:bg-gray-800/60 p-2 sm:p-0 rounded-xl border border-gray-100 dark:border-gray-700 sm:border-0">
              <input
                type="radio"
                name="syncType"
                checked={syncType === 'project'}
                onChange={() => setSyncType('project')}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-gray-700 dark:text-gray-300 font-medium truncate">Import Profile Project</span>
            </label>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                if (selectedFeedItemIds.size === filteredFeed.length) {
                  setSelectedFeedItemIds(new Set());
                } else {
                  setSelectedFeedItemIds(new Set(filteredFeed.map((x) => x.id)));
                }
              }}
              disabled={loadingFeed || filteredFeed.length === 0}
              className="flex-1 sm:flex-none px-3 py-2 text-xs md:text-sm font-semibold rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition"
            >
              {selectedFeedItemIds.size === filteredFeed.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              type="button"
              disabled={selectedFeedItemIds.size === 0 || syncing}
              onClick={handleSyncSubmit}
              className="flex-1 sm:flex-none px-4 py-2 text-xs md:text-sm font-bold rounded-xl text-white bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 hover:opacity-90 disabled:opacity-50 transition shadow-md whitespace-nowrap"
            >
              {syncing ? 'Syncing...' : `Sync Selected (${selectedFeedItemIds.size})`}
            </button>
          </div>
        </div>

        {/* Interactive Video Reel Preview & Rotation Dialog */}
        <AnimatePresence>
          {previewItem && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
              onClick={() => setPreviewItem(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-gray-900 rounded-3xl max-w-2xl w-full p-6 flex flex-col items-center space-y-4 shadow-2xl border border-gray-800"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-full flex justify-between items-center text-white border-b border-gray-800 pb-3">
                  <div className="flex items-center space-x-2">
                    <VideoCameraIcon className="w-5 h-5 text-purple-400" />
                    <h4 className="font-semibold text-lg">Reel Preview & Pre-Save Rotation</h4>
                  </div>
                  <button
                    onClick={() => setPreviewItem(null)}
                    className="p-1 hover:bg-gray-800 rounded-full text-gray-400 hover:text-white"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Video Display Container */}
                <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden flex items-center justify-center">
                  <CustomRotatedVideoPlayer
                    src={previewItem.media_url}
                    rotation={mediaRotations[previewItem.id] || 0}
                    title={previewItem.caption}
                  />
                </div>

                {/* Pre-Save Rotation Controls */}
                <div className="w-full flex items-center justify-between bg-gray-800/80 p-3 rounded-2xl">
                  <span className="text-xs text-gray-300 font-medium">
                    Current Rotation: <span className="text-indigo-400 font-bold">{mediaRotations[previewItem.id] || 0}°</span>
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleRotateItem(previewItem.id)}
                      className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold flex items-center space-x-1.5 transition"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      <span>Rotate +90°</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setMediaRotations((prev) => ({
                          ...prev,
                          [previewItem.id]: 0,
                        }))
                      }
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-xl text-xs font-medium transition"
                    >
                      Reset (0°)
                    </button>
                  </div>
                </div>

                <div className="w-full flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => {
                      const newSelection = new Set(selectedFeedItemIds);
                      newSelection.add(previewItem.id);
                      setSelectedFeedItemIds(newSelection);
                      setPreviewItem(null);
                      toast.success('Reel selected with rotation configuration!');
                    }}
                  >
                    Select & Close Preview
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Google Drive Connection Required Prompt Dialog */}
        <AnimatePresence>
          {showDrivePromptModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setShowDrivePromptModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full p-6 flex flex-col items-center text-center space-y-4 shadow-2xl border border-gray-100 dark:border-gray-800"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                  <VideoCameraIcon className="w-8 h-8" />
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                    Google Drive Connection Required
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                    To import Instagram Reels as profile videos, please connect your Google Drive account. The video files will be stored directly in your Google Drive and streamed when clicked.
                  </p>
                </div>

                <div className="flex flex-col w-full gap-2 pt-2">
                  <a
                    href={driveAuthUrl || '#'}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setShowDrivePromptModal(false)}
                    className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-2xl shadow-lg shadow-indigo-500/20 text-sm transition flex items-center justify-center space-x-2"
                  >
                    <span>Connect Google Drive</span>
                    <SparklesIcon className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowDrivePromptModal(false)}
                    className="w-full py-2.5 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium rounded-2xl text-xs transition"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>,
    document.body
  );
};
